import { useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import styles from './AuthConfigTab.module.css';
import { otpConfigApi } from '../../../../../lib/apiClient';
import { renderMarkdownToHtml } from '../../../../../shared/widget/markdown';

export interface OtpProviderConfigValues {
  otpGreeting: string;
  providerGreetings: Record<string, string>;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  smtpFrom: string;
}

interface Props {
  config: OtpProviderConfigValues;
  onChange: (partial: Partial<OtpProviderConfigValues>) => void;
}

const MASK_CHAR = '\u2022';
function isMaskedValue(val: string): boolean {
  return val.includes(MASK_CHAR);
}

export default function OtpProviderConfig({ config, onChange }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [passwordEdited, setPasswordEdited] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const otpGreetingRef = useRef<HTMLTextAreaElement>(null);

  const isPassSet = isMaskedValue(config.smtpPass) && !passwordEdited;
  const otpGreeting = config.providerGreetings?.otp ?? config.otpGreeting ?? '';

  const updateOtpGreeting = (value: string) => {
    onChange({ providerGreetings: { ...config.providerGreetings, otp: value } });
  };

  const wrapOtpGreetingSelection = (before: string, after: string) => {
    const ta = otpGreetingRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = otpGreeting.slice(start, end);
    const newText = otpGreeting.slice(0, start) + before + selected + after + otpGreeting.slice(end);
    updateOtpGreeting(newText);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(start + before.length + selected.length + after.length, start + before.length + selected.length + after.length);
    });
  };

  const handleTest = async () => {
    if (!testEmail.trim()) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await otpConfigApi.test({
        recipientEmail: testEmail.trim(),
        smtpHost: config.smtpHost.trim(),
        smtpPort: config.smtpPort,
        smtpUser: config.smtpUser.trim(),
        smtpPass: isPassSet ? undefined : config.smtpPass,
        smtpFrom: config.smtpFrom.trim(),
      });
      setTestResult({ success: true, message: result.message || 'Test email sent!' });
    } catch (err: any) {
      setTestResult({ success: false, message: err?.message || 'Test failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>OTP Greeting</h3>
        <div className={styles.field}>
          <label>Welcome message shown before email input</label>
          <div className={styles.greetingEditor}>
            <div className={styles.greetingToolbar}>
              <button type="button" className={styles.greetingToolBtn} title="Bold"
                onMouseDown={e => { e.preventDefault(); wrapOtpGreetingSelection('**', '**'); }}><strong>B</strong></button>
              <button type="button" className={styles.greetingToolBtn} title="Italic"
                onMouseDown={e => { e.preventDefault(); wrapOtpGreetingSelection('*', '*'); }}><em>I</em></button>
              <button type="button" className={styles.greetingToolBtn} title="Line break"
                onMouseDown={e => { e.preventDefault(); wrapOtpGreetingSelection('\n', ''); }}>↵</button>
            </div>
            <textarea ref={otpGreetingRef} rows={3} value={otpGreeting}
              onChange={e => updateOtpGreeting(e.target.value)}
              placeholder="Supports **bold**, *italic*, and line breaks" />
            {otpGreeting && (
              <div className={styles.greetingPreview}>
                <span className={styles.greetingPreviewLabel}>Preview</span>
                <div dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(otpGreeting) }} />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>SMTP Configuration</h3>
        <div className={styles.field}>
          <label>SMTP Host</label>
          <input type="text" value={config.smtpHost} placeholder="e.g., smtp.gmail.com"
            onChange={e => onChange({ smtpHost: e.target.value })} />
        </div>
        <div className={styles.field}>
          <label>SMTP Port</label>
          <input type="number" value={config.smtpPort}
            onChange={e => onChange({ smtpPort: parseInt(e.target.value) || 587 })} />
        </div>
        <div className={styles.field}>
          <label>SMTP Username</label>
          <input type="text" value={config.smtpUser} placeholder="e.g., user@gmail.com"
            onChange={e => onChange({ smtpUser: e.target.value })} />
        </div>
        <div className={styles.field}>
          <label>SMTP Password {isPassSet && <span className={styles.passSetHint}>(saved - leave blank to keep current)</span>}</label>
          <div className={styles.passwordField}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={isPassSet ? '' : config.smtpPass}
              placeholder={isPassSet ? '********' : 'Enter SMTP password'}
              onChange={e => { setPasswordEdited(true); onChange({ smtpPass: e.target.value }); }}
              onFocus={() => { if (isPassSet) { setPasswordEdited(true); onChange({ smtpPass: '' }); } }}
              autoComplete="new-password" name="smtp-config-secret"
              data-lpignore="true" data-1p-ignore="true" data-form-type="other"
            />
            <button className={styles.revealBtn} onClick={() => setShowPassword(p => !p)} type="button">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div className={styles.field}>
          <label>From Email</label>
          <input type="email" value={config.smtpFrom} placeholder="noreply@example.com"
            onChange={e => onChange({ smtpFrom: e.target.value })} />
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Test Email Delivery</h3>
        <div className={styles.field}>
          <label>Recipient Email</label>
          <input type="email" value={testEmail} placeholder="test@example.com"
            onChange={e => setTestEmail(e.target.value)} />
        </div>
        <button className={styles.testBtn} onClick={handleTest}
          disabled={testing || !config.smtpHost || !testEmail.trim()} type="button">
          {testing ? 'Sending...' : 'Send Test Email'}
        </button>
        {testResult && (
          <div className={`${styles.testResult} ${testResult.success ? styles.testSuccess : styles.testError}`}>
            {testResult.message}
          </div>
        )}
      </div>
    </>
  );
}
